import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, of, switchMap, throwError } from 'rxjs';

import { environment } from '@falcon/env';
import { CoefficientsConfig } from '@falcon/app/shared/models/company.model';
import { PageableResponsePayloadModel } from '@falcon/app/shared/models/pageable-response-payload.model';

import {
  CompanyRegistrationContactRequest,
  CompanyRegistrationLegalAgreementsRequest,
  CompanyRegistrationManualRequest,
  CompanyRegistrationOtpResponse,
  CompanyRegistrationOtpVerifyRequest,
  CompanyRegistrationOtpVerifyResponse,
  CompanyRegistrationPasswordRequest,
  CompanyRegistrationPasswordResponse,
  CompanyRegistrationPcCodeRequest,
  CompanyRegistrationRecord,
  CompanyRegistrationResponse,
  RegistrationCompanyFunctions,
  RegistrationCompanySearch,
  RegistrationMediaData,
  RegistrationStatus,
  RegistrationStep,
} from './company-registration.model';
import { MandateModel } from '@falcon/app/shared/models/mandate.model';

/** The admin queue is small and has no paging UI — one page holds the whole list. */
const PENDING_REGISTRATIONS_PAGE_SIZE = 100;
export const COMPANY_REGISTRATIONS_PUBLIC_API_URL = `${environment.publicApiURL}/company-registrations`;

@Injectable({ providedIn: 'root' })
export class CompanyRegistrationService {
  readonly #http = inject(HttpClient);

  readonly #contactStepPublicApi = COMPANY_REGISTRATIONS_PUBLIC_API_URL;
  readonly #contactStepApi = `${environment.apiURL}/company-registrations`;
  readonly #companiesApi = `${environment.apiURL}/companies`;

  previewCompanyByVat(vat: string): Observable<RegistrationCompanySearch> {
    return this.#http.get<RegistrationCompanySearch>(`${this.#contactStepPublicApi}/companies`, {
      params: { vat },
    });
  }

  submitContactStep(
    payload: CompanyRegistrationContactRequest
  ): Observable<CompanyRegistrationResponse> {
    return this.#http.post<CompanyRegistrationResponse>(this.#contactStepPublicApi, payload);
  }

  requestOtp(registrationId: string): Observable<CompanyRegistrationOtpResponse> {
    return this.#http.post<CompanyRegistrationOtpResponse>(
      `${this.#contactStepPublicApi}/${registrationId}/otp`,
      {}
    );
  }

  verifyOtp(
    registrationId: string,
    payload: CompanyRegistrationOtpVerifyRequest
  ): Observable<CompanyRegistrationOtpVerifyResponse> {
    return this.#http.post<CompanyRegistrationOtpVerifyResponse>(
      `${this.#contactStepPublicApi}/${registrationId}/otp/verify`,
      payload
    );
  }

  setPassword(
    registrationId: string,
    payload: CompanyRegistrationPasswordRequest
  ): Observable<CompanyRegistrationPasswordResponse> {
    return this.#http.post<CompanyRegistrationPasswordResponse>(
      `${this.#contactStepPublicApi}/${registrationId}/password`,
      payload
    );
  }

  itsmeCodeLinkUrl(registrationId: string): string {
    return `${this.#contactStepPublicApi}/${registrationId}/codeLink`;
  }

  linkIdCard(
    registrationId: string,
    front: RegistrationMediaData,
    back: RegistrationMediaData
  ): Observable<void> {
    return this.#putUpdatedRegistration(registrationId, record => ({
      ...record,
      currentStep: 'IDENTITY_DOCUMENT',
      // Without this the stale `nextStep` would still point at IDENTITY_DOCUMENT and a resumed
      // wizard (BCJ-20132) would drop the user back on the ID-card screen they just completed.
      nextStep: 'PC_CODE_SELECTION',
      owner: { ...record.owner, identificationDocument: { front, back } },
    }));
  }

  getCurrentRegistration(context?: HttpContext): Observable<CompanyRegistrationRecord> {
    return this.#http.get<CompanyRegistrationRecord>(`${this.#contactStepApi}/current`, {
      context,
    });
  }

  getRegistration(
    registrationId: string,
    context?: HttpContext
  ): Observable<CompanyRegistrationRecord> {
    return this.#http.get<CompanyRegistrationRecord>(`${this.#contactStepApi}/${registrationId}`, {
      context,
    });
  }

  getRegistrations(
    statuses: RegistrationStatus[],
    page = 0,
    size = PENDING_REGISTRATIONS_PAGE_SIZE
  ): Observable<PageableResponsePayloadModel<CompanyRegistrationRecord[]>> {
    return this.#http.get<PageableResponsePayloadModel<CompanyRegistrationRecord[]>>(
      this.#contactStepApi,
      { params: { statuses, page, size } }
    );
  }

  countPendingRegistrations(): Observable<number> {
    return this.getRegistrations(['REQUIRE_MANUAL_VALIDATION'], 0, 1).pipe(
      map(({ totalElements }) => totalElements)
    );
  }

  approveRegistration(registrationId: string): Observable<void> {
    return this.#http.post<void>(`${this.#contactStepApi}/${registrationId}/approve`, {});
  }

  rejectRegistration(registrationId: string): Observable<void> {
    return this.#http.post<void>(`${this.#contactStepApi}/${registrationId}/reject`, {});
  }

  flagManualValidation(registrationId: string): Observable<void> {
    return this.#putUpdatedRegistration(registrationId, record => ({
      ...record,
      status: 'REQUIRE_MANUAL_VALIDATION',
    }));
  }

  /**
   * Persist the manually-entered representative details and flag the registration for admin
   * validation.
   */
  submitManualRegistration(
    registrationId: string,
    payload: CompanyRegistrationManualRequest
  ): Observable<void> {
    return this.#putUpdatedRegistration(registrationId, record => ({
      ...record,
      ...payload,
      owner: { ...record.owner, ...payload.owner },
    }));
  }

  confirmItsmeIdentity(
    registrationId: string,
    payload: { nextStep: RegistrationStep; requireManualValidation: boolean }
  ): Observable<void> {
    return this.#putUpdatedRegistration(registrationId, record => ({
      ...record,
      currentStep: 'PERSONAL_DATA_AUTO',
      nextStep: payload.nextStep,
      ...(payload.requireManualValidation ? { status: 'REQUIRE_MANUAL_VALIDATION' as const } : {}),
    }));
  }

  submitPcCode(payload: CompanyRegistrationPcCodeRequest): Observable<void> {
    const { registrationId, pcCode, hasVca, currentStep, nextStep } = payload;

    return this.#putUpdatedRegistration(registrationId, record => ({
      ...record,
      currentStep,
      nextStep,
      company: {
        ...record.company,
        pcCode,
        hasVca: hasVca ?? undefined,
      },
    }));
  }

  submitFunctions(registrationId: string): Observable<void> {
    return this.#putUpdatedRegistration(registrationId, record => ({
      ...record,
      company: {
        ...record.company,
      },
      currentStep: 'FUNCTION_SELECTION',
      nextStep: 'AGREEMENT_ACCEPTANCE',
    }));
  }

  #putUpdatedRegistration(
    registrationId: string,
    update: (record: CompanyRegistrationRecord) => CompanyRegistrationRecord
  ): Observable<void> {
    return this.#putRegistrationRecord(registrationId, update).pipe(map(() => undefined));
  }

  #putRegistrationRecord(
    registrationId: string,
    update: (record: CompanyRegistrationRecord) => CompanyRegistrationRecord
  ): Observable<CompanyRegistrationRecord> {
    return this.getRegistration(registrationId).pipe(
      switchMap(record => {
        const updated = update(record);

        return this.#http
          .put<void>(`${this.#contactStepApi}/${registrationId}`, updated)
          .pipe(map(() => updated));
      })
    );
  }

  submitLegalAgreements(
    payload: CompanyRegistrationLegalAgreementsRequest
  ): Observable<{ status: RegistrationStatus }> {
    const { registrationId, agreements, currentStep, nextStep } = payload;

    return this.#putRegistrationRecord(registrationId, record => ({
      ...record,
      currentStep,
      nextStep,
      legalAgreements: agreements,
    })).pipe(
      switchMap(({ status }) => {
        if (!status) {
          return throwError(
            () => new Error('Registration status missing after legal agreements submission')
          );
        }
        return status === 'IN_PROGRESS'
          ? this.createCompany(registrationId).pipe(map(() => ({ status })))
          : of({ status });
      })
    );
  }

  editCompanyFunction(
    registrationId: string,
    updatedFunction: RegistrationCompanyFunctions
  ): Observable<void> {
    return this.#putUpdatedRegistration(registrationId, record => ({
      ...record,
      company: {
        ...record.company!,
        functions: (record.company!.functions ?? []).map(fn =>
          fn.occupationalProfileId === updatedFunction.occupationalProfileId ? updatedFunction : fn
        ),
      },
    }));
  }

  addCompanyFunction(
    registrationId: string,
    newFunction: RegistrationCompanyFunctions
  ): Observable<void> {
    return this.#putUpdatedRegistration(registrationId, record => ({
      ...record,
      company: {
        ...record.company!,
        functions: [...(record.company!.functions ?? []), newFunction],
      },
    }));
  }

  removeCompanyFunction(registrationId: string, functionId: string): Observable<void> {
    return this.#putUpdatedRegistration(registrationId, record => ({
      ...record,
      company: {
        ...record.company!,
        functions: (record.company!.functions ?? []).filter(
          fn => fn.occupationalProfileId !== functionId
        ),
      },
    }));
  }

  createCompany(registrationId: string): Observable<void> {
    return this.#http.post<void>(`${this.#contactStepApi}/${registrationId}/submit`, {});
  }

  getMandate(companyId: string, context?: HttpContext): Observable<MandateModel> {
    return this.#http.get<MandateModel>(`${this.#companiesApi}/${companyId}/mandate`, { context });
  }

  addMandate(companyId: string): Observable<void> {
    return this.#http.post<void>(`${this.#companiesApi}/${companyId}/mandate`, { companyId });
  }

  updateMandate(companyId: string, body: MandateModel): Observable<MandateModel> {
    return this.#http.put<MandateModel>(
      `${this.#companiesApi}/${companyId}/mandate`,
      this.mapBodyEmptyStringToNull(body)
    );
  }

  completeRegistration(registrationId: string): Observable<void> {
    return this.#putUpdatedRegistration(registrationId, record => ({
      ...record,
      status: 'COMPLETED',
    }));
  }

  getCoefficientsConfig(): Observable<CoefficientsConfig> {
    return this.#http.get<CoefficientsConfig>(`${this.#contactStepApi}/coefficients`);
  }

  protected mapBodyEmptyStringToNull(obj: Record<string, any>): Record<string, any> {
    return Object.keys(obj).reduce((mappedObj, key) => {
      const value = obj[key];

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return {
          ...mappedObj,
          [key]: { ...this.mapBodyEmptyStringToNull(value) },
        };
      }

      return {
        ...mappedObj,
        [key]: typeof value === 'string' && !value.trim().length ? null : value,
      };
    }, {});
  }
}
